/*
 * Copyright (C) 2024 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { ConvertForDataTestPipe } from '@common/tech-shared';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

@Component({
  selector: 'ods-checkbox',
  imports: [CommonModule, ReactiveFormsModule, ConvertForDataTestPipe],
  host: { class: 'block' },
  template: `
    <div class="relative flex items-start gap-3 text-start">
      <input
        type="checkbox"
        class="peer bg-whitetext focus-visible:border-background-200 disabled:border-disabled-dark disabled:bg-disabled mt-0.5 box-border size-5 shrink-0 appearance-none rounded-xs border outline-2 outline-offset-2 outline-transparent outline-solid hover:border-2 disabled:hover:border"
        [ngClass]="
          hasError ?
            'border-error hover:border-error focus-visible:outline-error'
          : 'border-primary hover:border-primary-hover focus-visible:outline-focus'
        "
        [formControl]="fieldControl"
        [attr.id]="inputId"
        [attr.disabled]="disabled ? true : null"
        [attr.data-test-id]="(label | convertForDataTest) + '-checkbox-editor'"
        [attr.aria-describedby]="ariaDescribedBy"
        (change)="inputChangeHandler($event)"
      />
      <label class="text-text leading-6" [attr.for]="inputId">{{ label }}</label>
      <svg
        viewBox="0 0 12 9"
        xmlns="http://www.w3.org/2000/svg"
        class="pointer-events-none absolute mt-0.5 hidden size-5 p-1 outline-hidden peer-checked:block"
        [ngClass]="hasError ? 'fill-error' : 'fill-primary focus-visible:fill-focus'"
      >
        <path d="M3.81353 7.10067L0.968732 4.30201L0 5.24832L3.81353 9L12 0.946309L11.0381 0L3.81353 7.10067Z" />
      </svg>
    </div>
    <ng-content select="[error]" />
  `,
})
export class CheckboxComponent {
  @Input() fieldControl: FormControl = new FormControl(false);
  @Input() inputId: string;
  @Input() label: string;
  @Input() set disabled(isDisabled: boolean) {
    this._disabled = isDisabled;
    if (isDisabled) {
      this.fieldControl.disable({ emitEvent: false });
    } else {
      this.fieldControl.enable({ emitEvent: false });
    }
  }
  @Input() hasError: boolean = false;
  @Input() ariaDescribedBy: string = null;

  @Output() inputChange = new EventEmitter<boolean>();

  _disabled: boolean = false;

  get disabled(): boolean {
    return this._disabled;
  }

  inputChangeHandler(event: Event) {
    this.inputChange.emit((event.target as HTMLInputElement).checked);
  }
}
